import {
  getFirestore,
  collectionGroup,
  getDocs,
  limit as fsLimit,
  orderBy,
  query,
  startAfter,
  type DocumentData,
  type QueryDocumentSnapshot,
  type Timestamp,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import {
  getStorage,
  ref as storageRef,
  listAll,
  getDownloadURL,
  getMetadata,
  ref,
} from "firebase/storage";
import type {
  Certification,
  CertificationReportFile,
  CertificationStatus,
  CertificationType,
  ListCertsParams,
  ListCertsPage,
  UpsertPatch,
} from "../models/certification";

// ───────── helpers ─────────
function isTs(x: unknown): x is Timestamp {
  return !!x && typeof (x as any).toMillis === "function";
}

function isNumEntry(e: [string, unknown]): e is [string, number] {
  return typeof e[1] === "number";
}

function parseCertification(
  snap: QueryDocumentSnapshot<DocumentData, DocumentData>,
  bucketType: CertificationType
): Certification | null {
  const d = snap.data() ?? {};
  const ownerUid = String(d.ownerUid ?? "");
  if (!ownerUid) return null;

  const out: Certification = {
    id: snap.id,
    ownerUid,
    type: bucketType,
    status: (d.status ?? "draft") as CertificationStatus,
  };

  if (typeof d.versionId === "string" && d.versionId)
    out.versionId = d.versionId;

  if (d.entity && typeof d.entity === "object") {
    const entRaw = d.entity as Record<string, unknown>;
    const ent: { nombre?: string; tipo?: string } = {};
    if (typeof entRaw.nombre === "string") ent.nombre = entRaw.nombre;
    if (typeof entRaw.tipo === "string") ent.tipo = entRaw.tipo;
    if (Object.keys(ent).length) out.entity = ent;
  }

  if (d.scores && typeof d.scores === "object") {
    const entries = Object.entries(d.scores as Record<string, unknown>);
    const filtered = Object.fromEntries(entries.filter(isNumEntry));
    if (Object.keys(filtered).length) out.scores = filtered; // Record<string, number>
  }

  if (isTs(d.createdAt)) out.createdAt = d.createdAt;
  if (isTs(d.updatedAt)) out.updatedAt = d.updatedAt;

  return out;
}

// ───────── list/paginación ─────────
type Bucket = {
  type: "official" | "trial";
  group: "evaluations" | "trialEvaluations";
};

export async function listCertificationsPage(
  params: ListCertsParams = { type: "official" }
): Promise<ListCertsPage> {
  const db = getFirestore();
  const t: "official" | "trial" | "all" = params.type ?? "official";
  const L = params.limit ?? 20;

  const buckets: Bucket[] =
    t === "all"
      ? [
          { type: "official", group: "evaluations" },
          { type: "trial", group: "trialEvaluations" },
        ]
      : t === "official"
      ? [{ type: "official", group: "evaluations" }]
      : [{ type: "trial", group: "trialEvaluations" }];

  const results: Certification[] = [];
  let nextCursor: QueryDocumentSnapshot<DocumentData, DocumentData> | null =
    null;

  let isFirstBucket = true;
  for (const b of buckets) {
    const base = query(
      collectionGroup(db, b.group),
      orderBy("updatedAt", "desc"),
      fsLimit(L)
    );

    const q2 =
      isFirstBucket && params.cursor
        ? query(base, startAfter(params.cursor))
        : base;

    const snaps = await getDocs(q2);

    for (const s of snaps.docs) {
      const cert = parseCertification(s, b.type);
      if (cert) results.push(cert);
    }

    if (isFirstBucket) {
      if (snaps.docs.length === L) {
        nextCursor = snaps.docs[snaps.docs.length - 1]!; // no undefined
      } else {
        nextCursor = null;
      }
      isFirstBucket = false;
    }
  }

  results.sort((a, b) => {
    const ta = a.updatedAt?.toMillis?.() ?? a.createdAt?.toMillis?.() ?? 0;
    const tb = b.updatedAt?.toMillis?.() ?? b.createdAt?.toMillis?.() ?? 0;
    return tb - ta;
  });

  return { items: results.slice(0, L), next: nextCursor };
}

// ───────── CRUD ─────────
export interface UpsertCertificationInput {
  ownerUid: string;
  type: CertificationType; // official -> evaluations ; trial -> trialEvaluations
  id: string;
  patch: UpsertPatch; // no incluir props con undefined
}

export async function upsertCertification(input: UpsertCertificationInput) {
  const db = getFirestore();
  const col = input.type === "official" ? "evaluations" : "trialEvaluations";
  const ref = doc(db, "users", input.ownerUid, col, input.id);
  await setDoc(ref, input.patch as Record<string, unknown>, { merge: true });
}

export async function updateCertificationFields(
  input: UpsertCertificationInput
) {
  const db = getFirestore();
  const col = input.type === "official" ? "evaluations" : "trialEvaluations";
  const ref = doc(db, "users", input.ownerUid, col, input.id);
  await updateDoc(ref, input.patch as Record<string, unknown>);
}

export async function deleteCertification(
  ownerUid: string,
  type: CertificationType,
  id: string
) {
  const db = getFirestore();
  const col = type === "official" ? "evaluations" : "trialEvaluations";
  await deleteDoc(doc(db, "users", ownerUid, col, id));
}

// ───────── Storage (reportes) ─────────
// Overloads para compatibilidad
export function listCertificationReports(params: {
  ownerUid: string;
  evaluationId: string;
}): Promise<CertificationReportFile[]>;
export function listCertificationReports(
  ownerUid: string,
  evaluationId: string
): Promise<CertificationReportFile[]>;

// Implementación única
export async function listCertificationReports(
  a: { ownerUid: string; evaluationId: string } | string,
  b?: string
): Promise<CertificationReportFile[]> {
  const ownerUid = typeof a === "string" ? a : a.ownerUid;
  const evaluationId = typeof a === "string" ? (b as string) : a.evaluationId;

  if (!ownerUid || !evaluationId) {
    throw new Error(
      "listCertificationReports requiere ownerUid y evaluationId."
    );
  }

  const storage = getStorage();
  const prefix = `users/${ownerUid}/evaluations/${evaluationId}/reports/`;
  const listRef = ref(storage, prefix);

  const res = await listAll(listRef);
  const files = await Promise.all(
    res.items.map(async (itemRef) => {
      const [meta, url] = await Promise.all([
        getMetadata(itemRef),
        getDownloadURL(itemRef),
      ]);
      return {
        path: itemRef.fullPath,
        name: itemRef.name,
        size: meta.size,
        updated: meta.updated ? new Date(meta.updated) : undefined,
        contentType: meta.contentType,
        downloadURL: url,
      } as CertificationReportFile;
    })
  );

  files.sort(
    (a, b) => (b.updated?.getTime() ?? 0) - (a.updated?.getTime() ?? 0)
  );
  return files;
}
